// samplingEventForm/inspections/actions.js

// Importing shared filter actions factory
import { makeWellsFiltersActions } from "../filter/samplingEventWellsFiltersActions";

// api helpers provided by aggregator:
// - getCurrentEvent()
// - setCurrentEvent(event)
// - bump(patch?)
// - setInspectionsUi(patchOrFn)
export const makeInspectionsActions = (api) => {
  // Create shared filter actions for the inspections tab
  // Factory expects (setUiFn, bump) - pass the UI setter and bump function
  const filterActions = makeWellsFiltersActions(api.setInspectionsUi, api.bump);

  return {
    // Spread in all shared filter actions (toggleFilterMenu, openFilterMenu, closeFilterMenu, applyFilters, resetFilters, setSort, resetSort, resetAll)
    ...filterActions,

    // UI controls specific to inspections
    setExpandedWell: (wellId) => {
      api.setInspectionsUi((prev) => ({
        ...prev,
        expandedWellId: prev.expandedWellId === wellId ? null : wellId,
      }));
      api.bump();
    },

    // Confirmation modal function
    openConfirmationModal: (wellId) => {
      api.setInspectionsUi((prev) => ({
        isConfirmationModalOpen: true,
        wellIdToConfirm: wellId,
      }));
      api.bump();
    },

    // Closing the confirmation modal
    closeConfirmationModal: () => {
      api.setInspectionsUi((prev) => ({
        isConfirmationModalOpen: false,
        wellIdToConfirm: null,
      }));
      api.bump();
    },

    // Validation and Mark Compete function wrapper for our well card components
    validateInspectionAndMarkComplete: (wellId, fieldsToValidate) => {
      const event = api.getCurrentEvent();
      if (!event) return { ok: false, error: "no_event" };

      const well = event.samplingEventWells.find((w) => w.id === wellId);
      if (!well) return { ok: false, error: "well_not_found" };

      return well.validateInspectionAndMarkComplete(fieldsToValidate);
    },

    // Actions for dealing with autosave and data class updates
    // fieldsAndValues is an array/list with the field names as the keys and the values as the values { field : values, field: values}, so we can have multiple fields sent to the service if needed and have the return broadcast updated as well.
    // Example: [{ field: "facilityMapEasyToFind", value: "Y" }, { field: "aboveGroundFlushMount", value: "Y" }]
    updateWellInspection: (wellId, fieldsAndValues, emitPatch = null) => {
      const event = api.getCurrentEvent();
      if (!event) return { ok: false, error: "no_event" };

      const well = event.samplingEventWells.find((w) => w.id === wellId);
      if (!well) return { ok: false, error: "well_not_found" };
      console.log("fieldsAndValues", fieldsAndValues);
      // Putting this here, because our switch case will update multiple fields sometimes based on user input and actions.
      const fieldsToUpdate = fieldsAndValues.map(({ field }) => field);

      // Big old switch case for field updates
      fieldsAndValues.forEach(({ field, value }) => {
        switch (field) {
          case "facilityMapEasyToFind":
            well.setFacilityMapEasyToFind(value);
            break;
          case "aboveGroundFlushMount":
            well.setAboveGroundFlushMount(value);
            break;
          case "freeOfDamage":
            well.setFreeOfDamage(value);
            break;
          case "legibleWellId":
            well.setLegibleWellId(value);
            break;
          case "casingProtected":
            well.setCasingProtected(value);
            break;
          case "casingSecure":
            well.setCasingSecure(value);
            break;
          case "weedsTrash":
            well.setWeedsTrash(value);
            break;
          case "surfacePadCracked":
            well.setSurfacePadCracked(value);
            break;
          case "surfacePadProneWaterPonding":
            well.setSurfacePadProneWaterPonding(value);
            break;
          case "wellSecureLocked":
            well.setWellSecureLocked(value);
            break;
          case "surveyGaugingMarks":
            well.setSurveyGaugingMarks(value);
            break;
          case "bottomConditions":
            well.setBottomConditions(value);
            break;
          case "inspectionComments":
            well.setInspectionComments(value);
            break;
          case "inspectionCompleted":
            well.setInspectionCompleted(value);
            break;
          case "inspectionCompletedBy":
            well.setInspectionCompletedBy(value);
            break;
          case "inspectionCompletedDate":
            well.setInspectionCompletedDate(value);
            break;
          case "measuredTotalDepth":
            well.setMeasuredTotalDepth(value);
            break;
          case "measuredTotalDepthNm":
            well.setMeasuredTotalDepthNm(value);
            // If value is true, set measuredTotalDepth to null
            if (value) {
              well.setMeasuredTotalDepth(null);
              fieldsToUpdate.push("measuredTotalDepth");
            } else {
              well.setMeasuredTotalDepthNmReason(value);
              fieldsToUpdate.push("measuredTotalDepthNmReason");
            }
            break;
          case "measuredTotalDepthNmReason":
            well.setMeasuredTotalDepthNmReason(value);
            break;
          case "measuredDepthToWater":
            well.setMeasuredDepthToWater(value);
            break;
          case "measuredDepthToWaterNm":
            well.setMeasuredDepthToWaterNm(value);
            // If value is true, set measuredDepthToWater to null
            if (value) {
              well.setMeasuredDepthToWater(null);
              fieldsToUpdate.push("measuredDepthToWater");
            } else {
              well.setMeasuredDepthToWaterNmReason(value);
              fieldsToUpdate.push("measuredDepthToWaterNmReason");
            }
            break;
          case "measuredDepthToWaterNmReason":
            well.setMeasuredDepthToWaterNmReason(value);
            break;
          case "inspectionFeaturesDepths":
            well.setInspectionFeaturesDepths(value);
            break;
          // This is the inspection napl sections
          case "lnaplPresentInspection":
            well.setLnaplPresentInspection(value);
            if (!value) {
              well.setLnaplThicknessInspection(null);
              fieldsToUpdate.push("lnaplThicknessInspection");
              well.setLnaplOdorInspection("");
              fieldsToUpdate.push("lnaplOdorInspection");
              well.setLnaplColorInspection("");
              fieldsToUpdate.push("lnaplColorInspection");
              well.setLnaplOtherInspection("");
              fieldsToUpdate.push("lnaplOtherInspection");
            }
            break;
          case "lnaplThicknessInspection":
            well.setLnaplThicknessInspection(value);
            break;
          case "lnaplOdorInspection":
            well.setLnaplOdorInspection(value);
            break;
          case "lnaplColorInspection":
            well.setLnaplColorInspection(value);
            break;
          case "lnaplOtherInspection":
            well.setLnaplOtherInspection(value);
            break;
          case "dnaplPresentInspection":
            well.setDnaplPresentInspection(value);
            if (!value) {
              well.setDnaplThicknessInspection(null);
              fieldsToUpdate.push("dnaplThicknessInspection");
              well.setDnaplOdorInspection("");
              fieldsToUpdate.push("dnaplOdorInspection");
              well.setDnaplColorInspection("");
              fieldsToUpdate.push("dnaplColorInspection");
              well.setDnaplOtherInspection("");
              fieldsToUpdate.push("dnaplOtherInspection");
            }
            break;
          case "dnaplThicknessInspection":
            well.setDnaplThicknessInspection(value);
            break;
          case "dnaplOdorInspection":
            well.setDnaplOdorInspection(value);
            break;
          case "dnaplColorInspection":
            well.setDnaplColorInspection(value);
            break;
          case "dnaplOtherInspection":
            well.setDnaplOtherInspection(value);
            break;
          default:
            return { ok: false, error: "invalid_field" };
        }
      });

      // Emit the patch to the server
      if (emitPatch) {
        // Apply the patch to the well and return the patch, only need to return the values of the updated fields
        const patch = well.baseWellPatch("update", fieldsToUpdate);
        emitPatch(patch);
      }

      api.bumpRevision();

      return { ok: true, well };
    },

    // Handling incoming broadcasts from the server
    /**
     * Handle incoming websocket broadcast for inspection
     *@param {object} patch - The patch data with snake_case keys
     *@param {function} applyPatchToObject - Helper to apply patch using setters
     */
    handleBroadcast: (patch, applyPatchToObject) => {
      const event = api.getCurrentEvent();
      if (!event) return;

      const { id, action } = patch;
      const wells = event.samplingEventWells || [];

      const well = wells.find((w) => w.id === id);
      if (!well) return;

      // Apply patch fields using setter methods
      applyPatchToObject(well, patch, ["id", "action", "sampling_event_id"]);
      api.bump();
    },
  };
};
